import { useEffect, useLayoutEffect } from "react"
import { Navigate, Outlet, Route, Routes } from "react-router-dom"

import "react-toastify/dist/ReactToastify.css"
import "@mdxeditor/editor/style.css"
import "assets/scss/globals.scss"

import { useAppDispatch, useAppSelector } from "store/hooks"
import { showTermsOfUseModal } from "store/modalSlise"

import { UserRole } from "utils/checkRoleType"
import { isRestrictedSubscriptionPlan } from "utils/isRestrictedSubscriptionPlan"
import { useCountry } from "hooks/useCountry"
import { useLoadContractorAccountStatuses } from "hooks/useLoadContractorAccountStatuses"
import { Modals } from "modals"
import { AuthLayout } from "layouts/AuthLayout"
import MainLayout from "layouts/MainLayout/MainLayout"
import { NotFound } from "components/common/NotFound"
import { PrivateRoute } from "components/common/PrivateRoute"
import { ChangePassword } from "pages/common/Auth/ChangePassword"
import { CreatePassword } from "pages/common/Auth/CreatePassword"
import { ForgotPassword } from "pages/common/Auth/ForgotPassword"
import { SignIn } from "pages/common/Auth/SignIn"
import { RedirectPage } from "pages/common/RedirectPage"
import SampleErrorPage from "pages/common/SampleErrorPage"
import AlertsPage from "pages/contractor/AlertsPage/AlertsPage"
import ContractorDashboard from "pages/contractor/ContractorDashboard"
import ContractorDevicesPage from "pages/contractor/ContractorDevicesPage/ContractorDevicesPage"
import { ContractorUsers } from "pages/contractor/ContractorUsers/ContractorUsers"
import {
  CustomerDashboardAlerts,
  CustomerDashboardDetails,
  CustomerDashboardLayout,
  CustomerDashboardLiveData,
  CustomerDashboardMessages,
  CustomerDashboardMonitoring,
  CustomerDashboardOverview,
  CustomerDashboardPerformanceTest,
  CustomerDashboardReports,
} from "pages/contractor/CustomerDashboard"
import { Customers } from "pages/contractor/Customers"
import IntelligenceReportsPage from "pages/contractor/IntelligenceReportsPage"
import { MessageCenter } from "pages/contractor/MessageCenter"
import { MessageHistory } from "pages/contractor/MessageHistory"
import { MessageReport } from "pages/contractor/MessageReport"
import { MessageTemplateAdd } from "pages/contractor/MessageTemplateAdd"
import MessageTemplateContainerLayout from "pages/contractor/MessageTemplateContainer/MessageTemplateContainerLayout"
import { MessageTemplateCreate } from "pages/contractor/MessageTemplateCreate"
import { MessageTemplateEdit } from "pages/contractor/MessageTemplateEdit"
import MonitoringPage from "pages/contractor/MonitoringPage/MonitoringPage"
import { NotificationsPage } from "pages/contractor/NotificationsPage"
import { Orders } from "pages/contractor/Orders"
import PerformanceTestHistoryPage from "pages/contractor/PerformanceTestHistoryPage/PerformanceTestHistoryPage"
import PerformanceTestPage from "pages/contractor/PerformanceTestPage/PerformanceTestPage"
import { Alerts as VendorAlerts } from "pages/vendor/Alerts"
import { Contractors } from "pages/vendor/Contractors"
import { Dashboard as VendorDashboard } from "pages/vendor/Dashboard"
import { Devices as VendorDevices } from "pages/vendor/Devices"
import { MasterPass } from "pages/vendor/MasterPass"
import { Orders as VOrders } from "pages/vendor/Orders"
import { Users } from "pages/vendor/Users"
import { WarehouseList } from "pages/vendor/WarehouseList"
import { Devices as WHDevices } from "pages/warehouse/Devices"
import { Orders as WHOrders } from "pages/warehouse/Orders"

import dayjs, { COUNTRY_TO_LOCALE_MAPPING } from "./lib/dayjs"

export const App = () => {
  const isUserAuthorized = useAppSelector(state => state.user.isUserAuthorized)
  const userProfile = useAppSelector(state => state.user.userProfile)
  const dispatch = useAppDispatch()
  const country = useCountry()

  useLoadContractorAccountStatuses()

  useEffect(() => {
    if (isUserAuthorized && userProfile?.terms !== true) {
      dispatch(showTermsOfUseModal(true))
    }
  }, [isUserAuthorized])

  useLayoutEffect(() => {
    const locale = COUNTRY_TO_LOCALE_MAPPING[country]
    dayjs.locale(locale)
  }, [country])

  const isSubscriptionPlanRestricted = isRestrictedSubscriptionPlan(userProfile)

  const userRole = userProfile?.type ?? 0

  const isContractor = [UserRole.ContractorManager, UserRole.ContractorAdmin, UserRole.ContractorEngineer].includes(
    userRole,
  )

  return (
    <MainLayout>
      <Routes>
        <Route
          element={
            <>
              <Outlet />
              <Modals />
            </>
          }
        >
          <Route path="auth" element={userProfile ? <Navigate to="/" replace /> : <AuthLayout />}>
            <Route path="sign-in" element={<SignIn />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="create-password" element={<CreatePassword />} />
          </Route>

          {/* Sample Error Route for Sentry Testing */}
          <Route path="sample-error" element={<SampleErrorPage />} />

          {isSubscriptionPlanRestricted && (
            <Route element={<PrivateRoute />}>
              <Route path="/" element={<MessageCenter hideUnavailableModal />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          )}

          {/* CONTRACTOR */}
          {isContractor && !isSubscriptionPlanRestricted && (
            <Route element={<PrivateRoute />}>
              <Route path="dashboard" element={<ContractorDashboard />} />
              <Route path="devices" element={<ContractorDevicesPage />} />
              <Route path="orders" element={<Orders />}></Route>
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id/*" element={<CustomerDashboardLayout />}>
                <Route index element={<CustomerDashboardOverview />} />
                <Route path="overview" element={<CustomerDashboardOverview />} />
                <Route path="details" element={<CustomerDashboardDetails />} />
                <Route path="monitoring" element={<CustomerDashboardMonitoring />} />
                <Route path="livedata" element={<CustomerDashboardLiveData />} />
                <Route path="alerts" element={<CustomerDashboardAlerts />} />
                <Route path="messages" element={<CustomerDashboardMessages />} />
                <Route path="perf-test" element={<CustomerDashboardPerformanceTest />} />
                <Route path="reports" element={<CustomerDashboardReports />} />
              </Route>
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="perf-test" element={<PerformanceTestPage />} />
              <Route path="perf-test-history" element={<PerformanceTestHistoryPage />} />
              <Route path="intelligence-reports" element={<IntelligenceReportsPage />} />
              <Route path="monitoring" element={<MonitoringPage />} />
              <Route path="messages" element={<Navigate to="center" replace />} />
              <Route path="master-pass" element={<MasterPass />} />
              <Route path="messages/*">
                <Route path="center" element={<MessageCenter />} />
                <Route path="history" element={<MessageHistory />} />
                <Route path="report" element={<MessageReport />} />
                <Route element={<MessageTemplateContainerLayout />}>
                  <Route path="create-template" element={<MessageTemplateCreate />} />
                  <Route path="edit-template" element={<MessageTemplateEdit />} />
                  <Route path="add-template" element={<MessageTemplateAdd />} />
                </Route>
              </Route>
            </Route>
          )}

          {[UserRole.ContractorManager, UserRole.ContractorAdmin].includes(userRole) &&
            !isSubscriptionPlanRestricted && (
              <Route element={<PrivateRoute />}>
                <Route path="users" element={<ContractorUsers />} />
              </Route>
            )}

          {userRole === UserRole.ContractorTechnician && !isSubscriptionPlanRestricted && (
            <Route element={<PrivateRoute />}>
              <Route path="customers/*" element={<Customers />} />
              <Route path="intelligence-reports" element={<IntelligenceReportsPage />} />
              <Route path="monitoring" element={<MonitoringPage />} />
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="master-pass" element={<MasterPass />} />
              <Route path="notifications" element={<NotificationsPage />} />
            </Route>
          )}

          {/* VENDOR ADMIN */}
          {userRole === UserRole.VendorAdmin && (
            <Route element={<PrivateRoute />}>
              <Route path="contractors" element={<Contractors />} />
              <Route path="warehouse-list" element={<WarehouseList />} />
              <Route path="orders" element={<VOrders />}></Route>
              <Route path="devices" element={<VendorDevices />} />
              <Route path="alerts" element={<VendorAlerts />} />
              <Route path="dashboard" element={<VendorDashboard />} />
              <Route path="master-pass" element={<MasterPass />} />
              <Route path="admin/*">
                <Route path="users" element={<Users />} />
              </Route>
              <Route path="admin" element={<Navigate to="users" replace />} />
            </Route>
          )}

          {/* SALES MANAGER */}
          {userRole === UserRole.SalesManager && (
            <Route element={<PrivateRoute />}>
              <Route path="contractors" element={<Contractors />} />
              <Route path="admin" element={<Navigate to="users" replace />} />
              <Route path="orders" element={<VOrders />}></Route>
              <Route path="devices" element={<VendorDevices />} />
              <Route path="alerts" element={<VendorAlerts />} />
              <Route path="dashboard" element={<VendorDashboard />} />
              <Route path="master-pass" element={<MasterPass />} />
            </Route>
          )}

          {/* WAREHOUSE MANAGER */}
          {userRole === UserRole.WarehouseManager && (
            <Route element={<PrivateRoute />}>
              <Route path="wh-orders" element={<WHOrders />} />
              <Route path="wh-devices" element={<WHDevices />} />
            </Route>
          )}

          <Route element={<PrivateRoute />}>
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/" element={<RedirectPage />} />
          <Route path="/mydashboard" element={<Navigate to="/" replace />} />
          <Route path="*" element={<RedirectPage />} />
        </Route>
      </Routes>
    </MainLayout>
  )
}
